// src/components/ai/WelcomeScreen.js
import { useState, useRef, useEffect, useCallback } from 'react';
import { BsSend } from 'react-icons/bs';

// ═══════════════════════════════════════════════
// شبکهٔ نقاط یکنواخت با حواشی خارج از صفحه و درخشش نقطه‌ای
// ═══════════════════════════════════════════════
const StarNetwork = () => {
  const canvasRef = useRef(null);
  const pointsRef = useRef([]);
  const neighborsRef = useRef([]);
  const flashRef = useRef({ index: -1, startTime: 0 });
  const animationRef = useRef(null);

  const CONFIG = {
    pointCount: 50,                 // تعداد نقاط
    maxNeighbors: 4,                // حداکثر خطوط متصل به هر نقطه
    dotRadius: 1.6,                 // شعاع نقطهٔ معمولی
    lineWidth: 0.6,
    flashInterval: 1000,            // هر ۱ ثانیه یک درخشش
    flashDuration: 250,             // طول درخشش (میلی‌ثانیه)
    overflowMargin: 0.2,            // نقاط تا ۲۰٪ بیرون از صفحه
    colors: {
      dot: 'rgba(180, 180, 190, 0.9)',
      line: 'rgba(255, 255, 255, 0.2)',    // خطوط سفید کمرنگ
      flashDot: 'rgba(255, 255, 230, 1)',
      flashHalo: 'rgba(255, 255, 200, 0.4)',
    },
  };

  // تولید نقاط با حاشیهٔ خارج از دید
  const generatePoints = useCallback((width, height, count) => {
    const margin = CONFIG.overflowMargin;
    const totalW = width * (1 + 2 * margin);   // پهنای مؤثر
    const totalH = height * (1 + 2 * margin);
    const offsetX = -margin * width;           // انتقال به مختصات کانواس
    const offsetY = -margin * height;

    const cellSize = Math.sqrt((totalW * totalH) / count);
    const cols = Math.max(1, Math.floor(totalW / cellSize));
    const rows = Math.max(1, Math.floor(totalH / cellSize));
    const cellW = totalW / cols;
    const cellH = totalH / rows;

    const pts = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (pts.length >= count) break;
        const x = c * cellW + Math.random() * cellW + offsetX;
        const y = r * cellH + Math.random() * cellH + offsetY;
        pts.push({ x, y });
      }
      if (pts.length >= count) break;
    }
    while (pts.length < count) {
      pts.push({
        x: Math.random() * totalW + offsetX,
        y: Math.random() * totalH + offsetY,
      });
    }

    // فاصلهٔ مجاز برای اتصال بر اساس اندازهٔ خانه‌ها
    const maxDist = 1.5 * cellSize;
    const neighbors = pts.map(() => []);
    for (let i = 0; i < pts.length; i++) {
      const dists = [];
      for (let j = 0; j < pts.length; j++) {
        if (i === j) continue;
        const dx = pts[i].x - pts[j].x;
        const dy = pts[i].y - pts[j].y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < maxDist) {
          dists.push({ index: j, dist: d });
        }
      }
      dists.sort((a, b) => a.dist - b.dist);
      neighbors[i] = dists.slice(0, CONFIG.maxNeighbors).map(d => d.index);
    }
    return { pts, neighbors };
  }, []);

  const draw = useCallback((ctx, width, height) => {
    const points = pointsRef.current;
    const neighborMap = neighborsRef.current;
    const { index: flashIdx, startTime } = flashRef.current;
    const now = performance.now();

    ctx.clearRect(0, 0, width, height);

    // پیشرفت درخشش (۰ ← ۱ ← ۰)
    let flashProgress = 0;
    if (flashIdx !== -1) {
      const elapsed = now - startTime;
      if (elapsed < CONFIG.flashDuration) {
        flashProgress = Math.sin((elapsed / CONFIG.flashDuration) * Math.PI);
      }
    }

    // ۱. رسم خطوط (بدون درخشش)
    for (let i = 0; i < points.length; i++) {
      const neighbors = neighborMap[i] || [];
      for (const j of neighbors) {
        if (j < i) continue; // جلوگیری از رسم دوباره
        ctx.beginPath();
        ctx.moveTo(points[i].x, points[i].y);
        ctx.lineTo(points[j].x, points[j].y);
        ctx.strokeStyle = CONFIG.colors.line;
        ctx.lineWidth = CONFIG.lineWidth;
        ctx.stroke();
      }
    }

    // ۲. رسم نقاط (با درخشش روی نقطهٔ انتخاب‌شده)
    points.forEach((p, i) => {
      const isFlash = i === flashIdx && flashProgress > 0;
      if (isFlash) {
        // نقطهٔ درخشان با هاله
        const radius = CONFIG.dotRadius * 2.2 * flashProgress + 1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = CONFIG.colors.flashDot;
        ctx.globalAlpha = flashProgress;
        ctx.fill();
        ctx.globalAlpha = 1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, radius * 1.8, 0, Math.PI * 2);
        ctx.fillStyle = CONFIG.colors.flashHalo;
        ctx.globalAlpha = flashProgress * 0.6;
        ctx.fill();
        ctx.globalAlpha = 1;
      } else {
        // نقطهٔ معمولی
        ctx.beginPath();
        ctx.arc(p.x, p.y, CONFIG.dotRadius, 0, Math.PI * 2);
        ctx.fillStyle = CONFIG.colors.dot;
        ctx.fill();
      }
    });
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    draw(ctx, canvas.width, canvas.height);
    animationRef.current = requestAnimationFrame(animate);
  }, [draw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w;
      canvas.height = h;
      const { pts, neighbors } = generatePoints(w, h, CONFIG.pointCount);
      pointsRef.current = pts;
      neighborsRef.current = neighbors;
    };

    resize();
    window.addEventListener('resize', resize);

    const triggerFlash = () => {
      const idx = Math.floor(Math.random() * pointsRef.current.length);
      flashRef.current = { index: idx, startTime: performance.now() };
    };
    triggerFlash();
    const interval = setInterval(triggerFlash, CONFIG.flashInterval);

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      clearInterval(interval);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [generatePoints, animate]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-screen h-screen pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};

// ═══════════════════════════════════════════════
// کامپوننت اصلی WelcomeScreen با گرادینت جدید
// ═══════════════════════════════════════════════
export default function WelcomeScreen({ onSend }) {
  const [query, setQuery] = useState('');

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (query.trim()) onSend(query);
  };

  return (
    <div
      className="relative flex flex-col items-center justify-center h-full px-4 text-center overflow-hidden"
      style={{
        background: 'linear-gradient(to top, #0F172A, #4C1D95, #2563EB, #0D9488, #FFFFFF)',
      }}
    >
      <StarNetwork />

      <div className="relative z-10 flex flex-col items-center justify-center w-full max-w-lg">
        <h1
          className="text-3xl font-extrabold text-white mb-3 drop-shadow-lg"
          style={{ fontFamily: 'Doran, sans-serif' }}
        >
          دستیار هوشمند فرزانه
        </h1>
        <p className="text-sm text-white/90 max-w-md mb-8 leading-relaxed drop-shadow-md">
          همراه شما در ژرف‌کاوی آثار و اندیشه‌های استاد مصطفی ملکیان.
          <br />
          بپرسید تا با هم بیاموزیم...
        </p>

        <div className="w-full backdrop-blur-xl bg-white/10 rounded-2xl p-4">
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="پرسش خود را بنویسید..."
            rows={2}
            className="w-full min-h-[56px] px-3 py-2 bg-transparent border-none outline-none text-sm resize-none text-white placeholder:text-white/50"
            style={{ fontFamily: 'Doran, sans-serif' }}
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={handleSubmit}
              disabled={!query.trim()}
              className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 ${
                query.trim()
                  ? 'bg-white hover:bg-white text-purple-700 shadow-lg shadow-purple-200/30 active:scale-95'
                  : 'bg-gray-500/50 text-gray-300 cursor-not-allowed'
              }`}
            >
              <BsSend size={16} />
            </button>
          </div>
        </div>

        <p className="text-xs text-white/60 mt-4">
          Enter برای ارسال &nbsp;·&nbsp; Shift+Enter برای خط جدید
        </p>
      </div>
    </div>
  );
}